import { inject, Injectable } from '@angular/core';
import { Observable, catchError, from, map, throwError } from 'rxjs';
import {
  MeasureUnitCreateRequest,
  MeasureUnitResponse,
  MeasureUnitUpdateRequest,
  MeasureUnitsApi,
} from '@tourfold/libraries/tf-api-client-public-v2';

/**
 * Thin Observable wrapper around the generated {@link MeasureUnitsApi}. The endpoint returns
 * the full tenant list (measure units are a small, hand-curated registry), so there is no
 * pagination here — the list component filters and paginates client-side.
 */
@Injectable({ providedIn: 'root' })
export class MeasureUnitsService {
  private readonly measureUnitsApi = inject(MeasureUnitsApi);

  getMeasureUnits(): Observable<MeasureUnitResponse[]> {
    return from(this.measureUnitsApi.getMeasureUnits()).pipe(
      map((response) =>
        [...response.measureUnits].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
      ),
      catchError((error) => throwError(() => error))
    );
  }

  createMeasureUnit(request: MeasureUnitCreateRequest): Observable<MeasureUnitResponse> {
    return from(
      this.measureUnitsApi.createMeasureUnit({ measureUnitCreateRequest: request })
    ).pipe(catchError((error) => throwError(() => error)));
  }

  updateMeasureUnit(
    measureUnitId: string,
    request: MeasureUnitUpdateRequest
  ): Observable<MeasureUnitResponse> {
    return from(
      this.measureUnitsApi.updateMeasureUnit({ measureUnitId, measureUnitUpdateRequest: request })
    ).pipe(catchError((error) => throwError(() => error)));
  }

  deleteMeasureUnit(measureUnitId: string): Observable<void> {
    return from(this.measureUnitsApi.deleteMeasureUnit({ measureUnitId })).pipe(
      catchError((error) => throwError(() => error))
    );
  }
}
